import { themeGet } from '@styled-system/theme-get';
import { Popover, InputNumber, Tooltip } from 'antd';
import React from 'react';
import styled, { useTheme } from 'styled-components';
import { AvailabilityIndicator } from '../../../logic/Models/Responses/SeedResponseV7';
import { ReactComponent as DeleteXButton } from '../../../assets/images/Xmark.svg';
import { ICropConfig } from '../../../logic/store/Seeds/CropsSlice';
import { Button } from '../../../components/Button/Button';
import { Price } from '../../../components/Pricing/Price';
import { AvailabilityIcon } from './AvailabilityIcon';
import { ReactComponent as CaretDownIcon } from '../../../components/Dropdown/Icons/CaretDownIcon.svg';


export const ProductRow = (props: {
	availability: AvailabilityIndicator;
	hybridName: string;

	/** Selected treatment. */
	treatmentKey: string;
	treatmentName: string;

	/** All treatments available for this product. */
	treatments: {
		/** Globally unique Id for treatment. */
		treatmentKey: string;
		treatmentName: string;
		availability: AvailabilityIndicator;
	}[];

	targetYield?: number;
	onTargetYieldChanged?: (targetYield: number) => void;
	/** There was an error fetching the seeding rates for a given target yield. */
	fetchRatesError?: string;
	onRefreshRates?: () => void;

	appliedAcres?: number;
	/** Number of bags. */
	quantity?: number;
	/** Seeds per acre */
	seedingRate?: number;

	/** Show pricing info or not. */
	showPricing: boolean;

	price: { 
		price: number | undefined; 
		pricingUnit: 'Bag' | 'Acre' | undefined 
	};
	totalPrice?: number;
	
	onDelete?: () => void;

	onUpdate: (update: {
		targetYield?: number;
		appliedAcres?: number;
		/** # of bags */
		quantity?: number;
		/** Seeds per acre */
		seedingRate?: number;
	}) => void;

	onUpdateTreatment: (treatmentId: string) => void;

	cropConfig: ICropConfig;
}
) => 
{
	const { 
		availability,
		hybridName,
		treatmentKey,
		treatmentName,
		cropConfig,
		targetYield,
		fetchRatesError,
		price,
		showPricing,
		appliedAcres,
		quantity: bags,
		seedingRate,
		totalPrice,
		treatments,
		onUpdate,
		onDelete,
	} = props;

	const name = `${hybridName}-${treatmentName}`;
	const theme = useTheme();
	const ArrowDown = styled(CaretDownIcon) `
		margin-left: 5px;
		width: 13px;
		height: 13px;
		border-radius: 3px;
		padding: 1.5px;
		background-color: ${theme.colors.lightGrey};
		path {
			fill: ${theme.colors.darkGrey};
		}
	`;

	const enterHandler = (existingValue: number | undefined, handler?: (value: number | undefined) => void) => 
		(e: React.KeyboardEvent<HTMLInputElement>) =>
		{
			if (!handler) return;

			const rawValue = (e.target as HTMLInputElement).value;
			if (rawValue)
				handler(Number(rawValue));
			else if (existingValue)
				handler(existingValue);
		};

	const blurHandler = (existingValue: number | undefined, handler?: (value: number | undefined) => void) => 
		(e: React.FocusEvent<HTMLInputElement>) =>
		{
			if (!handler) return;

			const rawValue = e.currentTarget.value;
			if (rawValue)
				handler(Number(rawValue));
			else if (existingValue)
				handler(existingValue);
		};
	
	const targetYieldInput = (
		<InputNumber
			type="number"
			min={cropConfig.minYield}
			max={cropConfig.maxYield}
			value={targetYield}
			bordered={false}
			onPressEnter={enterHandler(targetYield ?? 0, v => onUpdate({ targetYield: v }))}
			onBlur={blurHandler(targetYield ?? 0, v => onUpdate({ targetYield: v }))} 
		/>
	);

	const updateBagsIfNeeded = (newValue: number | undefined) => 
	{
		// Only trigger an update if the value has effectively changed
		// This prevents a recalculation of the input acres when the user tabs across
		if (newValue === undefined || newValue === (bags ?? 0))
			return;

		// If the user is only entering bags or acres and there is not yet a target yield, set the minimum
		if (!targetYield)
			onUpdate({ quantity: newValue, targetYield: cropConfig.minYield });
		else
			onUpdate({ quantity: newValue });
	};

	return (
		<StyledProductRow isLast={false}>
			<div className='FixedArea'>
				<AvailabilityIcon availability={availability} style={{ width: 40, }}/>
				<div className={'NameArea'} data-testid={`NameArea-${name}`}>
					<div className={'HybridName'}>{hybridName}</div>
					{treatments.length > 1 ? (
						<Popover 
							trigger='click'
							title={'Treatments'}
							content={(
								<div data-testid={`TreatmentPopup-${name}`} style={{ maxHeight: 300, overflow: 'auto'}}>
									{treatments.map(t => (
										<Tooltip key={t.treatmentKey} title={`Change treatment to ${t.treatmentName}`}>
											<Button 
												style={{
													width: '100%',
													marginBottom: 8,
													display:'flex',
													flexDirection: 'row', 
													alignItems:'center',
													fontSize: 12,
													fontWeight: 'normal',
												}} 
												disabled={t.treatmentKey === treatmentKey}
												variant='light' 
												data-testid={`TreatmentPopupItem-${t.treatmentKey}`}
												onClick={() => props.onUpdateTreatment(t.treatmentKey)}
											>
												<AvailabilityIcon availability={t.availability} style={{ width: 20 }}/> {t.treatmentName}
											</Button>
										</Tooltip>
									))}
								</div>
							)}>
							<Button variant='light' className='TreatmentName'  style={{ maxWidth: 175, width: 175, textAlign: 'left' }}>{treatmentName} <ArrowDown/></Button>
						</Popover>
					) : (
						<Tooltip title={'No other treatments available for this hybrid.'}>
							<Button variant='light' className='TreatmentName' style={{ maxWidth: 175, width: 175, textAlign: 'left' }}>{treatmentName}</Button>
						</Tooltip>
					)}
				</div>
			</div>
			<div className='FlexArea'>
				{/** Target yield. */}
				{cropConfig.hasYieldTarget && (
					<div className={'HybridYieldTarget'} data-testid={`HybridYieldTarget-${name}`}>
						{fetchRatesError ? 
							<InputErrorPopover
								title={fetchRatesError}
								message={fetchRatesError}
								buttonTitle='Refresh'
								onClick={() => props.onRefreshRates && props.onRefreshRates()}
							>
								{targetYieldInput} Target Yield
							</InputErrorPopover>
							: 
							<>
								{targetYieldInput} Target Yield
							</>
						}	
					</div>
				)}
				{/** Pricing */}
				{showPricing && (
					<div className={'HybridCostPerAcre'} data-testid={`HybridCostPerAcre-${name}`}>
						{price.price
							? <><Price price={price.price}/>/Unit</>
							: <span>---</span>
						}
					</div>
				)}
				{/** Applied Area */}
				<div className={'HybridArea'} data-testid={`HybridArea-${name}`}>
					<InputNumber
						type='number'
						min={0}
						value={appliedAcres}
						bordered={false}
						onPressEnter={enterHandler(appliedAcres, v => onUpdate({ appliedAcres: v }))}
						onBlur={blurHandler(appliedAcres, v => onUpdate({ appliedAcres: v }))}
					/>
					Acres
				</div>
				{/** Bags */}
				<div className={'HybridQuantity'} data-testid={`HybridQuantity-${name}`}>
					<InputNumber
						type='number'
						min={0}
						value={bags}
						bordered={false}
						onPressEnter={enterHandler(bags, updateBagsIfNeeded)}
						onBlur={blurHandler(bags, updateBagsIfNeeded)}
					/>
					Bags
				</div>
				{/** Seeding Rate */}
				<div className={'HybridSeedingRate'} data-testid={`HybridSeedingRate-${name}`}>
					{cropConfig.canEditSeedingRate ? (
						<>
							<span>Seeds/Ac:</span>
							<Tooltip 
								title={seedingRate === undefined
									? 'Please enter a seeding rate'
									: seedingRate < cropConfig.minSeedingRate ? 
										`Please enter seeding rate higher than ${cropConfig.minSeedingRate}` 
										: seedingRate > cropConfig.maxSeedingRate ? 
											`Please enter seeding rate lower than ${cropConfig.maxSeedingRate}` 
											: ''}
							>
								<InputNumber
									type='number'
									min={cropConfig.minSeedingRate}
									max={cropConfig.maxSeedingRate}
									value={seedingRate}
									bordered={false}
									onPressEnter={enterHandler(seedingRate, v => onUpdate({ seedingRate: v }))}
									onBlur={blurHandler(seedingRate, v => onUpdate({ seedingRate: v }))}
								/>
							</Tooltip>
						</>
					): (
						<>Seeds/Ac: {seedingRate ? seedingRate : 0}</>
					)}
				</div>
				{/** Total price */}
				{showPricing && (
					<div className={'HybridTotalPrice'} data-testid={`HybridTotalPrice-${name}`}>
						Total: &nbsp; {totalPrice !== undefined && <Price price={totalPrice} />}
					</div>
				)}
				{/** Delete button */}
				<div style={{ width: 10}}>
					{onDelete && <DeleteXButton style={{ cursor: 'pointer' }} onClick={onDelete} />}
				</div>
			</div>
		</StyledProductRow>
	);
};

/** Wrap an input in a red box and show a pop up on click with the error message and a button to trigger a remediation. */
const InputErrorPopover = (props: {
	title:string;
	message:string;
	buttonTitle: string;
	onClick: () => void;
	children: React.ReactNode;
}) => 
{
	return (
		<div style={{ border: '1px solid red', padding: '0 10px' }}>
			<Popover 
				trigger='click'
				title={props.title}
				content={(
					<div>
						<span>{props.message}</span>
						<Button variant='outlined' onClick={props.onClick}> {props.buttonTitle} </Button>
					</div>
				)}>
				{props.children}
			</Popover>
		</div>
	);
};

const StyledProductRow = styled.div<{ isLast: boolean }>`
	display: flex;
	flex-direction: row;
	width: 100%;
	min-height: 55px;
	border: 1px solid;
	border-color: ${themeGet('colors.lightestGrey')};
	align-items: stretch;
	border-bottom-left-radius: ${p => (p.isLast) ? 5 : 0}px;
	border-bottom-right-radius: ${p => (p.isLast) ? 5 : 0}px;
	margin-bottom: ${p => (p.isLast) ? 8 : 0}px;
	
	.FixedArea {
		display: flex;
		flex-direction: row;
		flex-grow: 0;
		flex-shrink: 0;
		align-items: stretch;

		>div {
			font-size: ${themeGet('fontSizes.extraSmall')};
			font-family: ${themeGet('fonts.base')};
			color: ${themeGet('colors.darkGrey')};
		}

		.NameArea {
			display: flex;
			flex-direction: column;
			align-items: start;
			justify-content: center;
			padding-left: 10px;

			.HybridName {
				display: flex;
				flex-direction: row;
				align-items: center;
				font-weight: ${themeGet('fontWeights.bold')};
				width: 160px;
			}
			.TreatmentName {
				height: auto;
				font-weight: normal;
			}
		}
	}

	.FlexArea {
		display: flex;
		flex-direction: row;
		flex-grow: 1;
		align-items: center;
		flex-wrap: wrap;

		.ant-input-number-handler-wrap
		{
			display: none;
		}

		.ant-alert-error {
			padding: 2px 8px;
		}

		>div {
			font-size: ${themeGet('fontSizes.extraSmall')};
			font-family: ${themeGet('fonts.base')};
			color: ${themeGet('colors.darkGrey')};
			flex-grow: 0;
			flex-shrink: 0;
			width: 120px;
			flex-grow: 1;
			display:flex;
			flex-direction: row;
			justify-content: center;
			align-items: center;
		}
	}

	.ant-input-number-input {
		padding: 0;
		text-align: center;
	}

	.ant-input-number.ant-input-number-borderless
	{
		width: 50px;

		input {
			font-family: 'Roboto';
			font-size: 12px;
			height: 14px;
			border-bottom: 1px black solid;
		}
	}

	.HybridSeedingRate .ant-input-number.ant-input-number-borderless
	{
		width: 70px;
	}
`;